/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import * as _ from 'lodash';
import { STATUS_LIST, TASK_PRIORITY_KEYED, GLOBAL_APPROVAL_STATUS_KEYED, TASK_TABLE_ENUMS } from './enums';
import * as DateUtils from './DateUtils';
import * as TaskUtils from './TaskUtilities';
import { pendingAcceptanceIconSVG, acceptedIconSVG } from './constants';
import { parseJSONStringToObject } from './UtilityFunctions';
import { userIcon, equipmentResourceIcon } from '../common/constants';

const taskTypeCellStyle = (params) => {
  const borderColor = params.data.isPlanned ? 'rgb(154, 127, 201)' : 'rgb(220, 194, 95)';
  return { borderLeft: `3px solid ${borderColor}`, cursor: `pointer` };
};

const parentTaskNameCellStyle = (params) => {
  const borderColor = _.get(params, 'data.parentTask.color');
  if (!borderColor) return {};
  return { borderLeft: `3px solid ${borderColor}`, cursor: `pointer` };
};
const delayMinutesTypeCellStyle = (params) => {
  return params.value ? { color: 'red' } : {};
};
export const convertDurationToHoursAndMinutes = (timeDuration, timeUnit) => {
  let timeDurationInSeconds = timeDuration;
  if (_.isNil(timeDurationInSeconds) || !_.isFinite(parseInt(timeDurationInSeconds, 10))) {
    return ``;
  }
  timeDurationInSeconds = parseInt(timeDurationInSeconds, 10);
  if (timeUnit === 'minutes' || timeUnit === 'minute') {
    timeDurationInSeconds *= 60;
  }
  if (timeUnit === 'hours' || timeUnit === 'hour') {
    timeDurationInSeconds *= 3600;
  }
  if (!timeDurationInSeconds) return `0 hours`;
  const hours = parseInt(timeDurationInSeconds / 3600, 10);
  const minutes = parseInt((timeDurationInSeconds - hours * 3600) / 60, 10);
  const seconds = parseInt(timeDurationInSeconds - hours * 3600 - minutes * 60, 10);
  let timeDurationStr = ``;
  if (hours && hours === 1) timeDurationStr = `${timeDurationStr} ${hours} hour,`;
  else if (hours && hours > 1) timeDurationStr = `${timeDurationStr} ${hours} hours,`;
  if (minutes && minutes === 1) timeDurationStr = `${timeDurationStr} ${minutes} minute,`;
  else if (minutes && minutes > 1) timeDurationStr = `${timeDurationStr} ${minutes} minutes,`;
  if (seconds && seconds === 1) timeDurationStr = `${timeDurationStr} ${seconds} second,`;
  else if (seconds && seconds > 1) timeDurationStr = `${timeDurationStr} ${seconds} seconds,`;
  timeDurationStr = timeDurationStr.substr(timeDurationStr, timeDurationStr.length - 1);
  return timeDurationStr;
};
const UnderlinedTextCellRenderer = (params) => {
  return <span style={{ textDecoration: 'underline' }}>{params.value ?? ''}</span>;
};

const StatusCellRenderer = (params) => {
  const STATUS_LIST_KEYBY_TEXT = _.keyBy(Object.values(STATUS_LIST), 'text');
  const statusData = STATUS_LIST_KEYBY_TEXT[params.value];

  const isValidStatus = !_.isEmpty(TaskUtils.taskStringStatusConditioning(params.value, STATUS_LIST_KEYBY_TEXT));

  if (!isValidStatus || !statusData) return null;

  const buttonStyle = {
    backgroundColor: statusData.color,
    border: `1px solid ${statusData.borderColor}`,
    borderRadius: '3px',
    font: 'inherit',
    lineHeight: '20px',
    padding: '3px 8px',
    minWidth: '107px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return <button style={buttonStyle}>{statusData.text}</button>;
};

const PriorityCellRenderer = (params) => {
  const value = TaskUtils.taskStringPriorityConditioning(params.value, TASK_PRIORITY_KEYED, params.id);

  if (!_.isEmpty(value) && TASK_PRIORITY_KEYED[value]) {
    const { backgroundColor, borderColor, text } = TASK_PRIORITY_KEYED[value];

    const buttonStyle = {
      backgroundColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '3px',
      minWidth: '75px',
      padding: '3px 8px',
      font: 'inherit',
      lineHeight: '20px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    return <button style={buttonStyle}>{text}</button>;
  }

  return null;
};

const DescriptionCellRenderer = (params) => {
  const value = params.value || '';

  const style = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return <span style={style}>{value}</span>;
};

const CellRendererWithAwaitingAssigneeAcceptanceIcon = (params) => {
  const isTaskAwaitingAssigneeAcceptance = TaskUtils.isTaskAwaitingAssigneeAcceptance(params.data);
  const seqNo = params.value ?? '';

  const wrapperStyle = {
    padding: '3px 7px',
    borderRadius: '50px',
    background: '#e8e8e8',
  };

  const imageStyle = { position: 'relative', top: '2px', marginRight: '4px' };

  if (isTaskAwaitingAssigneeAcceptance) {
    return (
      <span style={wrapperStyle}>
        <img
          src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(pendingAcceptanceIconSVG())}`}
          alt="Pending Acceptance"
          style={imageStyle}
        />
        {seqNo}
      </span>
    );
  }

  if (_.get(params, 'data.isAcceptedByAssignee')) {
    return (
      <span style={wrapperStyle}>
        <img
          src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(acceptedIconSVG())}`}
          alt="Accepted"
          style={imageStyle}
        />
        {seqNo}
      </span>
    );
  }

  if (seqNo) {
    return <span style={wrapperStyle}>{seqNo}</span>;
  }

  return null;
};

export const getTimeDurationCellRenderer = (value, timeUnit) => convertDurationToHoursAndMinutes(value, timeUnit);
export const BLANK_STRING = '%blank%';
const joyn2AssigneeCustomComparator = (filter, assigneeFullNameStr, filterText) => {
  const filterTextLoweCase = filterText.toLowerCase();
  const assigneeFullNames = assigneeFullNameStr.split('$');
  switch (filter) {
    case 'contains':
      return assigneeFullNames.some((a) => a.toLowerCase().includes(filterTextLoweCase));
    case 'equals':
      if (filterTextLoweCase === BLANK_STRING) {
        return !assigneeFullNameStr.length;
      }
      return assigneeFullNames.some((a) => a.toLowerCase() === filterTextLoweCase);
    default:
      // should never happen
      console.warn(`invalid filter type ${filter}`);
      return false;
  }
};

const ApprovalCellRenderer = (params) => {
  const approvalInfo = _.get(params, 'value', []);

  const approversTextList = approvalInfo.flatMap((val) =>
    _.get(val, 'approvers', []).map(
      (approver) =>
        `${val.displayName}|${approver.approverLimit}|${_.get(
          GLOBAL_APPROVAL_STATUS_KEYED,
          `[${approver.approvalStatus}].label`,
          'Pending Approval'
        )}`
    )
  );

  return (
    <div>
      {approversTextList.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </div>
  );
};

const ApprovalStatusCellRenderer = (params) => {
  const approvalStatus =
    _.get(GLOBAL_APPROVAL_STATUS_KEYED, `${params.value}`) || GLOBAL_APPROVAL_STATUS_KEYED.pendingapproval;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        className="status-indicator"
        style={{
          backgroundColor: approvalStatus.backgroundColor,
          border: `1px solid ${approvalStatus.borderColor}`,
          width: 10,
          height: 10,
          borderRadius: '50%',
          display: 'inline-block',
        }}
      />
      {approvalStatus.label}
    </span>
  );
};

function isTaskAwaitingAcceptance(task) {
  return _.get(task, 'taskType.needAcceptance') && task.status === STATUS_LIST.NOT_READY.key;
}

const AssigneeCellRenderer = (params) => {
  const taskNeedEquipment = params.data?.taskType?.needEquipment || false;

  if (!params.data?.assigneeIds?.length) return null;

  const [assignee1] = params.data.assigneeIds;

  const isAccepted = assignee1.isAccepted;
  const isAwaiting = isTaskAwaitingAcceptance(params.data);

  const icon = isAccepted ? (
    <img
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(acceptedIconSVG())}`}
      alt="accepted"
      style={{ position: 'relative', top: '2px', marginRight: 4 }}
    />
  ) : isAwaiting ? (
    <img
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(pendingAcceptanceIconSVG())}`}
      alt="pending"
      style={{ position: 'relative', top: '2px', marginRight: 4 }}
    />
  ) : null;

  const resourceIcon = taskNeedEquipment ? (
    <img
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        assignee1.isEquipmentResource ? equipmentResourceIcon : userIcon
      )}`}
      alt="resource"
      style={{ position: 'relative', top: '2px', marginRight: 4 }}
    />
  ) : null;

  const assigneeText = (
    <span
      style={{
        padding: '3px 7px',
        borderRadius: '50px',
        background: '#e8e8e8',
        marginRight: '4px',
        cursor: 'default',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
      {resourceIcon}
      {assignee1.fullName}
    </span>
  );

  const additionalCount = params.data.assigneeIds.length - 1;

  const countText = additionalCount > 0 && (
    <span
      style={{
        padding: '3px 7px',
        borderRadius: '50px',
        background: '#e8e8e8',
        cursor: 'pointer',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        style={{
          padding: '1px 4px',
          borderRadius: '50px',
          background: '#424f62',
          color: 'white',
          fontSize: '10px',
        }}
      >
        + {additionalCount}
      </span>
    </span>
  );

  return (
    <span>
      {assigneeText}
      {countText}
    </span>
  );
};

const ReminderCellRenderer = (params) => {
  const value = params.value;

  if (!value) return null;

  return (
    <span>
      <span className="reminder-indicator" /> {value}
    </span>
  );
};

export default ReminderCellRenderer;

const PriorityAliasRenderer = (params) => {
  const priority = _.get(params, 'data.priority');
  const value = params.value;

  if (!value || !TASK_PRIORITY_KEYED[priority]) return null;

  const { backgroundColor, borderColor } = TASK_PRIORITY_KEYED[priority];

  const buttonStyle = {
    backgroundColor,
    border: `1px solid ${borderColor}`,
    borderRadius: '3px',
    minWidth: '75px',
    padding: '3px 8px',
    font: 'inherit',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return <button style={buttonStyle}>{value}</button>;
};

const TaskNumberCellRenderer = (params) => {
  const taskNumber = params.value;

  if (!taskNumber) return null;

  return (
    <span>
      {taskNumber} <span className="fa fa-copy p-cursor"></span>
    </span>
  );
};

export const EXPORT_MANAGEMENT_TASK_MAP = [
  {
    headerName: 'Task Id',
    field: 'id',
    hide: true,
  },

  {
    headerName: 'Asset Name',
    field: 't_asset_name',
    filter: 'agTextColumnFilter',
  },

  {
    headerName: 'Priority',
    field: 't_priority',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Status',
    field: 't_status',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Description',
    field: 't_desc',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Created By',
    field: 't_createdBy',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Created On',
    field: 't_createdOn',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Linked Tasks',
    field: 't_linkedTask_numbers',
    filter: 'agTextColumnFilter',
    hide: true,
  },

  {
    headerName: 'Task Number',
    field: 't_taskNumber',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Approved By',
    field: 't_approvedBy',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Approved On',
    field: 't_approvedOn',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Modified By',
    field: 't_modifiedBy',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Modified On',
    field: 't_modifiedOn',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Resolved By',
    field: 't_resolvedBy',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Resolved On',
    field: 't_resolvedOn',
    filter: 'agTextColumnFilter',
    hide: true,
  },

  {
    headerName: 'AssignedOn',
    field: 't_assignedOn',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Resolved Via',
    field: 't_resolvedvia',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Resolved At',
    field: 't_resolvedat',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  {
    headerName: 'Start Time',
    field: 't_startTime',
    filter: 'agTextColumnFilter',
    hide: true,
  },
];

export const TASK_TABLE_COLUMN_MAP = {
  id: {
    headerName: 'Task Id',
    field: 'id',
  },
  seqNo: {
    headerName: 'Seq #',
    field: 'seqNo',
    filter: 'agNumberColumnFilter',
    cellRenderer: CellRendererWithAwaitingAssigneeAcceptanceIcon,
    width: 70,
  },
  assignedToUserName: {
    headerName: 'Assigned To',
    field: 'assignedToUserName',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals'],
      textCustomComparator: joyn2AssigneeCustomComparator,
      debounceMs: 500,
    },
    valueGetter: (params) => (_.get(params, `data.assigneeIds`) ?? []).map((a) => a.fullName).join('$'),
    cellRenderer: AssigneeCellRenderer,
  },
  priorityScore: {
    headerName: 'Score',
    field: 'priorityScore',
    filter: 'agNumberColumnFilter',
    hide: true,
  },
  area: {
    headerName: 'Area',
    field: 'area',
    filter: 'agTextColumnFilter',
    valueGetter: (params) => _.get(params, `data.${TASK_TABLE_ENUMS.area}`) ?? [].map((name) => name).join('$'),
    hide: true,
  },
  assignedVendor: {
    headerName: 'Assigned Vendor',
    field: 'assignedVendor',
    filter: 'agTextColumnFilter',
    valueGetter: (params) =>
      (_.get(params, `data.assigneeIds`) ?? [])
        .filter((a) => a.vendorName)
        .map((a) => a.vendorName)
        .join(', '),
  },
  watchersName: {
    headerName: 'Watchers',
    field: 'watchersName',
    filter: 'agTextColumnFilter',
  },
  taskTypeName: {
    headerName: 'Task Type',
    field: 'taskTypeName',
    cellStyle: taskTypeCellStyle,
    cellRenderer: UnderlinedTextCellRenderer,
    filter: 'agTextColumnFilter',
    valueGetter: (params) => _.get(params, `data.${TASK_TABLE_ENUMS.taskTypeName}`),
  },
  parentTaskName: {
    headerName: 'Parent',
    field: 'parentTaskName',
    cellStyle: parentTaskNameCellStyle,
    cellRenderer: UnderlinedTextCellRenderer,
    valueGetter: (params) => _.get(params, `data.${TASK_TABLE_ENUMS.parentTaskName}`) ?? '',
  },
  createdOn: {
    headerName: 'Created On',
    field: 'createdOn',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
    valueFormatter: (x) => DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(x.value),
  },
  createdBy: { headerName: 'Created By', field: 'createdBy', filter: 'agTextColumnFilter', hide: true },
  modifiedOn: {
    headerName: 'Modified On',
    field: 'modifiedOn',
    filter: 'agTextColumnFilter',
    hide: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
    valueFormatter: (x) => DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(x.value),
  },
  modifiedBy: { headerName: 'Modified By', field: 'modifiedBy', filter: 'agTextColumnFilter', hide: true },
  resolvedOn: {
    headerName: 'Resolved On',
    field: 'resolvedOn',
    filter: 'agTextColumnFilter',
    hide: true,
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
    valueFormatter: (x) => (x.value ? DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(x.value) : ''),
  },
  resolvedBy: { headerName: 'Resolved By', field: 'resolvedBy', filter: 'agTextColumnFilter', hide: true },
  startTime: {
    headerName: 'Start Time',
    field: 'startTime',
    filter: 'agTextColumnFilter',
    valueFormatter: (x) => DateUtils.convertUTCDateStringToYYYYMMDDHHMMSSFormat(x.value),
  },
  duration: {
    headerName: 'Default Duration',
    field: 'duration',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
    valueFormatter: (params) => (params.value ? getTimeDurationCellRenderer(params.value, 'minutes') : ''),
  },
  delayMinutes: {
    headerName: 'Delayed By',
    field: 'delayMinutes',
    filter: 'agTextColumnFilter',
    cellStyle: delayMinutesTypeCellStyle,
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
    valueFormatter: (params) => (params.value ? getTimeDurationCellRenderer(params.value, 'minutes') : ''),
  },
  workordernumber: {
    headerName: 'Work Order #',
    field: 'workordernumber',
    filter: 'agTextColumnFilter',
    valueGetter: (params) => _.get(params, `data.${TASK_TABLE_ENUMS.workordernumber}`) ?? '',
  },
  eventSource: { headerName: 'Source', field: 'eventSource', filter: 'agTextColumnFilter' },
  stopName: {
    headerName: 'Stop',
    field: 'stopName',
    filter: 'agTextColumnFilter',
    valueGetter: (params) => _.get(params, `data.${TASK_TABLE_ENUMS.stopName}`) ?? '',
  },
  assetName: {
    headerName: 'Asset',
    field: 'assetName',
    filter: 'agTextColumnFilter',
    valueGetter: (params) =>
      _.get(params, TASK_TABLE_ENUMS.assetName) !== 'stop' ? _.get(params, 'data.asset.name') : '',
  },
  status: {
    headerName: 'Status',
    field: 'status',
    cellRenderer: StatusCellRenderer,
    filter: 'agTextColumnFilter',
    valueGetter: (params) => {
      const STATUS_LIST_TEXT_KEYED = _.keyBy(Object.values(STATUS_LIST), 'key');
      return TaskUtils.taskStringStatusConditioning(
        _.get(params, `data.${TASK_TABLE_ENUMS.status}`),
        STATUS_LIST_TEXT_KEYED,
        _.get(params, `data.id`)
      );
    },
    // STATUS_LIST_TEXT_KEYED[_.get(params, `data.${TASK_TABLE_ENUMS.status}`)].text,
  },
  priority: {
    headerName: 'Priority',
    field: 'priority',
    cellRenderer: PriorityCellRenderer,
    filter: 'agTextColumnFilter',
  },
  priorityAlias: {
    headerName: 'Priority Alias',
    field: 'priorityAlias',
    filter: 'agTextColumnFilter',
    cellRenderer: PriorityAliasRenderer,
    valueGetter: (params) => {
      let priorityAlias = _.get(params, 'data.taskType.priorityAlias');
      if (_.get(params, 'data.parentTaskId')) {
        priorityAlias = _.get(params, 'data.parentTask.taskType.priorityAlias');
      }
      const parsedPriorityAlias = parseJSONStringToObject(priorityAlias);
      return _.get(parsedPriorityAlias, `values.${_.get(params, 'data.priority')}`);
    },
  },
  timeSpent: {
    headerName: 'Time Spent(Actual)',
    field: 'timeSpent',
    filter: 'agTextColumnFilter',
    valueGetter: (params) =>
      _.get(params, 'data.data.s_timespent')
        ? getTimeDurationCellRenderer(_.get(params, 'data.data.s_timespent'), 'seconds')
        : '',
  },
  startDate: {
    headerName: 'Start Date',
    field: 'startDate',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
  },
  endDate: {
    headerName: 'End Date',
    field: 'endDate',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains', 'equals', 'notEquals', 'notContains'],
    },
  },
  frequency: { headerName: 'Frequency', field: 'frequency', filter: 'agTextColumnFilter' },
  nextRecurrenceDate: { headerName: 'Next Recurrence Date', field: 'nextRecurrenceDate', filter: 'agTextColumnFilter' },
  prevRecurrenceDate: {
    headerName: 'Previous Recurrence Date',
    field: 'prevRecurrenceDate',
    filter: 'agTextColumnFilter',
  },
  actionButtons: {
    headerName: 'Actions',
    field: 'actionButtons',
    cellRenderer: 'actionsColumnRenderer',
    filter: false,
  },
  checkBoxColumn: {
    headerName: '',
    width: 60,
    field: 'checkBoxColumn',
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    valueGetter: () => '',
  },
  description: {
    headerName: 'Description',
    field: 'description',
    cellRenderer: DescriptionCellRenderer,
    filter: 'agTextColumnFilter',
    width: 150,
    tooltipField: 'description',
    tooltipComponent: 'descriptionTooltipComponent',
    valueGetter: (params) => _.get(params, 'data.data.s_desc'),
  },
  approvalInfo: {
    headerName: 'Approval Matrix, Amount, Limit and status',
    field: 'approvalInfo',
    cellRenderer: ApprovalCellRenderer,
    filter: 'agTextColumnFilter',
    width: 300,
  },
  globalApprovalStatus: {
    headerName: 'Global Approval Status',
    field: 'approvalStatus',
    cellRenderer: ApprovalStatusCellRenderer,
    filter: 'agTextColumnFilter',
    width: 150,
  },
  workRequestName: {
    headerName: 'Work Request',
    field: 'taskTypeName',
    cellStyle: taskTypeCellStyle,
    cellRenderer: UnderlinedTextCellRenderer,
    filter: 'agTextColumnFilter',
    width: 150,
    valueGetter: (params) => _.get(params, 'data.taskType.name') ?? '',
  },
  attachmentCount: {
    headerName: 'Attachment Count',
    field: 'attachmentCount',
    filter: 'agTextColumnFilter',
    valueGetter: (params) => _.get(params, 'data.attachmentCount') ?? '',
  },
  taskNumber: {
    headerName: 'Task #',
    field: 'taskNumber',
    filter: 'agTextColumnFilter',
    cellRenderer: TaskNumberCellRenderer,
  },
  acceptRejectStatus: { headerName: 'Accept/Reject Status', field: 'acceptRejectStatus', filter: 'agTextColumnFilter' },
  reminder: {
    headerName: 'Reminder',
    field: 'reminder',
    cellRenderer: ReminderCellRenderer,
    filter: 'agTextColumnFilter',
  },
  recurrenceInactive: {
    headerName: 'On/Off',
    field: 'recurrenceInactive',
    cellRenderer: 'CheckboxExampleToggle',
    filter: 'agTextColumnFilter',
    width: 90,
    cellClass: 'center-class-aggrid',
  },
};
export const USER_MANAGEMENT_TABLE_COLUMN_MAP = {
  actionButtons: {
    headerName: 'Edit',
    field: 'actionButtons',
    minWidth: 80,
    cellRenderer: 'actionsColumnRenderer',
  },
  checkbox: {
    headerName: '',
    checkboxSelection: true,
    resizable: false,
    sortable: false,
    minWidth: 50,
    field: 'checkbox',
  },
  name: {
    headerName: 'Name',
    field: 'name',
    minWidth: 100,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      debounceMs: 500,
      trimInput: true,
      suppressAndOrCondition: true,
    },
  },
  userName: {
    headerName: 'Username',
    field: 'userName',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      debounceMs: 500,
      trimInput: true,
      suppressAndOrCondition: true,
    },
    sortable: true,
    minWidth: 100,
  },
  vendorName: {
    headerName: 'Vendor',
    field: 'vendorName',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  appVersion: {
    headerName: 'App Version',
    field: 'appVersion',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  locationPermission: {
    headerName: 'Location Permission',
    field: 'locationPermission',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  fieldName: {
    headerName: 'Field Name',
    field: 'fieldName',
    filter: false,
    minWidth: 100,
    sortable: false,
  },
  timeZone: {
    headerName: 'Time Zone',
    field: 'timeZone',
    valueFormatter: (x) => (x.value ? x.value.replace(/_/g, ' ') : ''),
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  startTime: {
    headerName: 'Start Time',
    field: 'startTime',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  endTime: {
    headerName: 'End Time',
    field: 'endTime',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  hitchStartDate: {
    headerName: 'Hitch Start Date',
    field: 'hitchStartDate',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
  offDays: {
    headerName: 'Off Days',
    field: 'offDays',
    width: 150,
    filter: false,
    sortable: false,
  },
  teamNames: {
    headerName: 'JOYNFSM Team(s)',
    field: 'teamNames',
    filter: false,
    sortable: false,
  },
  joynFSMRoleNames: {
    headerName: 'JOYNFSM Role(s)',
    field: 'joynFSMRoleNames',
    filter: false,
    sortable: false,
  },
  areaNames: {
    headerName: 'JOYNFSM Area(s)',
    field: 'areaNames',
    filter: false,
    sortable: false,
  },
  createdBy: {
    headerName: 'Created By',
    field: 'createdBy',
    filter: false,
    hide: true,
    width: 100,
  },
  createdOn: {
    headerName: 'Created On',
    field: 'createdOn',
    width: 100,
    filter: false,
    hide: true,
    sortable: true,
  },
  modifiedBy: {
    headerName: 'Modified By',
    field: 'modifiedBy',
    filter: false,
    hide: true,
    sortable: false,
  },
  modifiedOn: {
    headerName: 'Modified On',
    field: 'modifiedOn',
    filter: false,
    width: 100,
    hide: true,
    sortable: true,
  },
  equipmentResource: {
    headerName: 'Equipment Resource',
    field: 'equipmentResource',
    minWidth: 100,
    filter: false,
    sortable: false,
  },
};
const COMMON_TASK_TABLE_COLUMN_ORDER = [
  'assignedToUserName',
  'assignedVendor',
  'taskTypeName',
  'parentTaskName',
  'description',
  'status',
  'reminder',
  'acceptRejectStatus',
  'taskNumber',
  'workordernumber',
  'priority',
  'priorityAlias',
  'priorityScore',
  'stopName',
  'assetName',
  'watchersName',
  'area',
  'timeSpent',
  'duration',
  'startTime',
  'eventSource',
  'delayMinutes',
  'attachmentCount',
  'modifiedOn',
  'modifiedBy',
  'createdOn',
  'createdBy',
  'resolvedOn',
  'resolvedBy',
];
export const DEFAULT_TASK_TABLE_COLUMN_ORDER = [
  'checkBoxColumn',
  'actionButtons',
  'seqNo',
  ...COMMON_TASK_TABLE_COLUMN_ORDER,
];
export const DEFAULT_LINKED_TASK_LIST_TABLE_COLUMN_ORDER = ['actionButtons', ...COMMON_TASK_TABLE_COLUMN_ORDER];
export const DEFAULT_USER_MANAGEMENT_TABLE_COLUMN_ORDER = [
  'actionButtons',
  'checkbox',
  'name',
  'userName',
  'vendorName',
  'appVersion',
  'locationPermission',
  'joynFSMRoleNames',
  'fieldNames',
  'startTime',
  'endTime',
  'timeZone',
  'equipmentResource',
  'hitchStartDate',
  'offDays',
  'teamNames',
  'areaNames',
  'createdBy',
  'createdOn',
  'modifiedBy',
  'modifiedOn',
];
export const DEFAULT_RECURRING_TASK_TABLE_COLUMN_ORDER = [
  'checkBoxColumn',
  'taskTypeName',
  'stopName',
  'assetName',
  'area',
  'startDate',
  'endDate',
  'frequency',
  'nextRecurrenceDate',
  'prevRecurrenceDate',
  'actionButtons',
  'recurrenceInactive',
];
export const REQUEST_TABLE_COLUMN_ORDER = [
  'checkBoxColumn',
  'actionButtons',
  'approvalInfo',
  'workRequestName',
  'globalApprovalStatus',
  'description',
  'workordernumber',
  'priority',
  'status',
  'stopName',
  'assetName',
  'modifiedOn',
  'modifiedBy',
  'createdOn',
  'createdBy',
];
export const LOCAL_STORAGE_TASK_LIST_TABLE_ORDER_KEY = 'TASK_LIST_TABLE_ORDER_KEY';
export const LOCAL_STORAGE_TASK_TABLE_COLUMN_WIDTH_KEY = 'TASK_TABLE_COLUMN_WIDTH_KEY';
export const LOCAL_STORAGE_TASK_TABLE_COLUMN_VISIBILITY_KEY = 'TASK_TABLE_COLUMN_VISIBILITY_KEY';
export const LOCAL_STORAGE_RECURRING_TASK_LIST_TABLE_ORDER_KEY = 'RECURRING_TASK_LIST_TABLE_ORDER_KEY';
export const LOCAL_STORAGE_RECURRING_TASK_TABLE_COLUMN_WIDTH_KEY = 'RECURRING_TASK_TABLE_COLUMN_WIDTH_KEY';
export const LOCAL_STORAGE_RECURRING_TASK_TABLE_COLUMN_VISIBILITY_KEY = 'RECURRING_TASK_TABLE_COLUMN_VISIBILITY_KEY';
export const LOCAL_STORAGE_USER_MANAGEMENT_TABLE_ORDER_KEY = 'USER_MANAGEMENT_TABLE_ORDER_KEY';
export const LOCAL_STORAGE_USER_MANAGEMENT_TABLE_COLUMN_WIDTH_KEY = 'USER_MANAGEMENT_TABLE_COLUMN_WIDTH_KEY';
export const LOCAL_STORAGE_USER_MANAGEMENT_TABLE_COLUMN_VISIBILITY_KEY = 'USER_MANAGEMENT_TABLE_COLUMN_VISIBILITY_KEY';
export const LOCAL_STORAGE_LINKED_TASK_LIST_TABLE_COLUMN_VISIBILITY_KEY =
  'LINKED_TASK_LIST_TABLE_COLUMN_VISIBILITY_KEY';
export const LOCAL_STORAGE_LINKED_TASK_LIST_TABLE_COLUMN_WIDTH_KEY = 'LINKED_TASK_LIST_TABLE_COLUMN_WIDTH_KEY';
export const LOCAL_STORAGE_LINKED_TASK_LIST_TABLE_ORDER_KEY = 'LOCAL_STORAGE_LINKED_TASK_LIST_TABLE_ORDER_KEY';
export const TASK_LIST_FILTER_MODEL = 'TASK_LIST_FILTER_MODEL';
export const TASK_LIST_SORT_MODEL = 'TASK_LIST_SORT_MODEL';
export const RECURRING_TASK_LIST_FILTER_MODEL = 'RECURRING_TASK_LIST_FILTER_MODEL';
export const RECURRING_TASK_LIST_SORT_MODEL = 'RECURRING_TASK_LIST_SORT_MODEL';
export const LOCAL_STORAGE_TASK_LIST_FILTER_MODEL = 'TASK_LIST_FILTER_MODEL';
export const LOCAL_STORAGE_TASK_LIST_TEMP_FILTER = 'TASK_LIST_TEMP_FILTER';
export const LOCAL_STORAGE_TASK_LIST_SORT_MODEL = 'TASK_LIST_SORT_MODEL';
export const LOCAL_STORAGE_FIELD_LIST = 'FIELD_LIST_USER_MANAGEMENT';
export const LOCAL_STORAGE_TASK_PIN_COLUMN_LIST = 'TASK_PIN_COLUMN_LIST';
// Asset list Const
export const ASSET_LIST_KEY = 'ASSET_LIST_KEY';
export const ASSET_LIST_TABLE_ORDER_KEY = 'ASSET_LIST_TABLE_ORDER_KEY';
export const ASSET_LIST_COLUMN_WIDTH_KEY = 'ASSET_LIST_COLUMN_WIDTH_KEY';
export const ASSET_LIST_COLUMN_VISIBILITY_KEY = 'ASSET_LIST_COLUMN_VISIBILITY_KEY';
// User list const
export const USER_LIST_KEY = 'USER_LIST_KEY';

export const onDisplayedColumnsChanged = (localStorageKey, userConfigUpdateFn, userConfigObjKey) => (params) => {
  const saveDataInLocalStorage = !userConfigUpdateFn || !userConfigObjKey;

  const visibleColumns = params.columnApi
    .getAllDisplayedColumns()
    .map((c) => _.get(c, 'colDef.field'))
    .filter((c) => !!c);
  const columnsMap = {};
  visibleColumns.forEach((col) => {
    columnsMap[col] = true;
  });
  const invisibleColumns = params.api
    .getColumnDefs()
    .map((c) => c.field)
    .filter((c) => !!c && !visibleColumns.includes(c));
  invisibleColumns.forEach((col) => {
    columnsMap[col] = false;
  });
  if (saveDataInLocalStorage) {
    localStorage.setItem(localStorageKey, JSON.stringify(columnsMap));
  } else {
    userConfigUpdateFn(userConfigObjKey, {
      [localStorageKey]: JSON.stringify(columnsMap),
    });
  }
};

export const onColumnMoved = (localStorageKey) => (params) => {
  let columnOrder = params.columnApi
    .getAllGridColumns()
    .map((c) => _.get(c, 'userProvidedColDef.field'))
    .filter((c) => !!c);
  columnOrder = _.uniq(columnOrder);
  localStorage.setItem(localStorageKey, JSON.stringify(columnOrder));
};

const maintainColumnorder = (columnOrder, pinnedColMap) => {
  let tempColumnOrderRightPined = [];
  let tempColumnOrderLeftPined = [];
  Object.keys(pinnedColMap).forEach((pin) => {
    const pinnedCols = pinnedColMap[pin];
    if (pinnedColMap[pin]) {
      pinnedCols.forEach((col) => {
        const index = columnOrder.indexOf(col);
        if (index > -1) {
          if (pin === 'left') {
            tempColumnOrderLeftPined[index] = col;
          } else if (pin === 'right') {
            tempColumnOrderRightPined[index] = col;
          }
        }
      });
    }
  });
  tempColumnOrderRightPined = tempColumnOrderRightPined.filter((val) => !_.isNil(val));
  tempColumnOrderLeftPined = tempColumnOrderLeftPined.filter((val) => !_.isNil(val));
  pinnedColMap.left = tempColumnOrderLeftPined;
  pinnedColMap.right = tempColumnOrderRightPined;
};

export const onColumnPinned = (localStorageKey, userConfigUpdateFn, userConfigObjKey, userConfig) => (params) => {
  const saveDataInLocalStorage = !userConfigUpdateFn || !userConfigObjKey || !userConfig;

  let pinnedColMap = saveDataInLocalStorage
    ? JSON.parse(localStorage.getItem(localStorageKey) || '{}')
    : parseJSONStringToObject(userConfig?.[userConfigObjKey]?.[localStorageKey] || '{}');
  pinnedColMap = JSON.parse(JSON.stringify(pinnedColMap));
  const columnOrder = params.columnApi
    .getAllGridColumns()
    .map((c) => _.get(c, 'userProvidedColDef.field'))
    .filter((c) => !!c);
  params.columns.forEach((col) => {
    const index = columnOrder.indexOf(_.get(col, 'colDef.field'));
    if (index > -1) {
      if (col.pinned === 'left') {
        if (!pinnedColMap.left) {
          pinnedColMap.left = [];
        }
        pinnedColMap.left.push(_.get(col, 'colDef.field'));
      } else if (col.pinned === 'right') {
        if (!pinnedColMap.right) {
          pinnedColMap.right = [];
        }
        pinnedColMap.right.push(_.get(col, 'colDef.field'));
      } else if (pinnedColMap.right && pinnedColMap.right.includes(_.get(col, 'colDef.field'))) {
        const ind = pinnedColMap.right.indexOf(_.get(col, 'colDef.field'));
        pinnedColMap.right.splice(ind, 1);
      } else if (pinnedColMap.left && pinnedColMap.left.includes(_.get(col, 'colDef.field'))) {
        const ind = pinnedColMap.left.indexOf(_.get(col, 'colDef.field'));
        pinnedColMap.left.splice(ind, 1);
      }
      maintainColumnorder(columnOrder, pinnedColMap);
    }
  });
  if (saveDataInLocalStorage) {
    localStorage.setItem(localStorageKey, JSON.stringify(pinnedColMap));
  } else {
    userConfigUpdateFn(userConfigObjKey, {
      [localStorageKey]: JSON.stringify(pinnedColMap),
    });
  }
};

export const onColumnResized = (localStorageKey, userConfigUpdateFn, userConfigObjKey) => (params) => {
  const saveDataInLocalStorage = !userConfigUpdateFn || !userConfigObjKey;

  if (params.source === 'autosizeColumns' && !params.column) {
    console.log('column resize update');
    // for cloud storage, only autosizeColumns needs to be handled
    const widthMap = {};
    params.columns.forEach((col) => {
      const fieldId = col.userProvidedColDef && col.userProvidedColDef.field;
      if (!fieldId) return;
      widthMap[fieldId] = col.actualWidth;
    });
    if (saveDataInLocalStorage) {
      localStorage.setItem(localStorageKey, JSON.stringify(widthMap));
    } else {
      userConfigUpdateFn(userConfigObjKey, {
        [localStorageKey]: JSON.stringify(widthMap),
      });
    }
  }

  if (params.column && saveDataInLocalStorage) {
    console.log('column resize update only local');

    const fieldId = params.column.userProvidedColDef && params.column.userProvidedColDef.field;
    if (!fieldId) return;
    const width = params.column.actualWidth;
    const widthMap = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    Object.assign(widthMap, { [fieldId]: width });
    localStorage.setItem(localStorageKey, JSON.stringify(widthMap));
  }
};

export const onDragStopped =
  (columnMovedLocalStorageKey, columnResizedLocalStorageKey, userConfigUpdateFn, userConfigObjKey) => (params) => {
    console.log('column Drag stopped');

    const saveDataInLocalStorage = !userConfigUpdateFn || !userConfigObjKey;

    const updatedValues = {};

    const allGridColumns = params.columnApi.getAllGridColumns();

    let columnOrder = allGridColumns.map((c) => _.get(c, 'userProvidedColDef.field')).filter((c) => !!c);

    columnOrder = _.uniq(columnOrder);

    const widthMap = {};

    allGridColumns
      .filter((c) => _.get(c, 'userProvidedColDef.field'))
      .forEach((c) => {
        widthMap[_.get(c, 'userProvidedColDef.field')] = c.actualWidth;
      });

    if (saveDataInLocalStorage) {
      localStorage.setItem(columnMovedLocalStorageKey, JSON.stringify(columnOrder));
      localStorage.setItem(columnResizedLocalStorageKey, JSON.stringify(widthMap));
    } else {
      updatedValues[columnMovedLocalStorageKey] = JSON.stringify(columnOrder);
      updatedValues[columnResizedLocalStorageKey] = JSON.stringify(widthMap);
    }

    userConfigUpdateFn(userConfigObjKey, updatedValues);
  };

export const GRID_OPTIONS = {
  modules: [
    ClientSideRowModelModule,
    CsvExportModule,
    ExcelExportModule,
    MenuModule,
    ClipboardModule,
    SetFilterModule,
    StatusBarModule,
    ColumnsToolPanelModule,
  ],
  headerHeight: 41,
  rowHeight: 42,
  defaultColDef: {
    filter: true,
    sortable: true,
    resizable: true,
    filterParams: {
      debounceMs: 500,
      // newRowsAction: 'keep',
    },
  },
  statusBar: {
    statusPanels: [
      {
        statusPanel: 'agTotalAndFilteredRowCountComponent',
        align: 'left',
      },
    ],
  },
};

export const TASK_GRID_OPTIONS = {
  getRowNodeId: (data) => data.id,
  immutableData: true,
  deltaRowDataMode: true,
  suppressScrollOnNewData: false, // Change this to false
  suppressHorizontalScroll: false,
  alwaysShowHorizontalScroll: false,
  alwaysShowVerticalScroll: true, // Force vertical scrollbar
};

export const TASK_VIEW_TASK_GRID_OPTIONS = {
  ...TASK_GRID_OPTIONS,
  getRowHeight: () => {
    // if (params.data.seqNo !== undefined) {
    return 40;
    // }
    // return Math.max(params.data.assigneeIds.length * 40, 40);
  },
  onRowDataUpdated: (params) => {
    setTimeout(() => {
      params.api.forEachNode((row) => {
        let height = 40;
        if (row.data.seqNo !== undefined) {
          height = 40;
        }
        row.setRowHeight(height);
        //   // height = Math.max(row.data.assigneeIds.length * 40, 40);
        // row.setRowHeight(height);
      });
      params.api.onRowHeightChanged();
      // params.api.resetRowHeights();
      params.api.redrawRows();
    }, 0);
  },
};

export const TASK_TYPE_VIEW_TASK_GRID_OPTIONS = {
  ...GRID_OPTIONS,
  getRowHeight: (params) => {
    if (params.data.priorityAlias !== undefined) {
      return 40;
    }
    return Math.max(params.data.priorityAlias.length * 40, 40);
  },
  onRowDataUpdated: (params) => {
    params.api.resetRowHeights();
    params.api.redrawRows();
  },
};

export const INFINITE_ROW_GRID_OPTIONS = {
  modules: [
    InfiniteRowModelModule,
    CsvExportModule,
    ExcelExportModule,
    MenuModule,
    ClipboardModule,
    SetFilterModule,
    StatusBarModule,
    ColumnsToolPanelModule,
  ],
  headerHeight: 41,
  rowHeight: 42,

  // Add these for proper resizing and scrolling
  suppressHorizontalScroll: false,
  alwaysShowVerticalScroll: true,
  suppressScrollOnNewData: false,

  defaultColDef: {
    filter: true,
    sortable: false,
    resizable: true,
    minWidth: 100, // Important for resizing
  },
  statusBar: {
    statusPanels: [
      {
        statusPanel: 'agTotalAndFilteredRowCountComponent',
        align: 'left',
      },
    ],
  },
};
